import React, { useMemo, useState } from 'react';
import { Button, Form, Icon, Message, Modal } from 'semantic-ui-react';
import { ErrorMessage, Field, FieldProps, Formik } from 'formik';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import useAxios from 'axios-hooks';
import { IBuilding } from '@relab/alstom-ptw-shared';

import { buildingUpsertAction } from '../../redux/actions';
import { TranslationComponent } from '../TranslationComponent';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { MapAreaSelector } from '../MapAreaSelector';

const BuildingUpsertSchema = (t: typeof i18n.t) =>
  Yup.object().shape({
    name: Yup.string().min(2, t('Too Short!')).max(50, t('Too Long!')).required(t('Name is required')),
    buildingArea: Yup.string().required(t('Campo richiesto')),
  });

interface BuildingUpsertModalProps {
  building?: IBuilding;
  buildingUpsertAction: typeof buildingUpsertAction;
}

const PBuildingUpsertModal: React.FC<BuildingUpsertModalProps> = ({ building, buildingUpsertAction }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const [{ loading, error }, executeUpsert] = useAxios(
    {
      url: `/building`,
      method: 'POST',
    },
    { manual: true }
  );

  const handleModalOpen = () => setModalOpen(true);

  const handleModalClose = () => setModalOpen(false);

  const handleSubmit = (values: Partial<IBuilding>) => {
    const { name, buildingArea } = values;

    executeUpsert({
      data: {
        ...building,
        name,
        buildingArea,
      },
    }).then(({ data }) => {
      buildingUpsertAction(data);
      handleModalClose();
    });
  };

  const { t } = useTranslation();

  const validationSchema = useMemo(() => BuildingUpsertSchema(t), [t]);

  return (
    <Formik
      initialValues={{
        name: building?.name ?? '',
        buildingArea: building?.buildingArea,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, isSubmitting }) => (
        <Modal
          trigger={
            <Button icon onClick={handleModalOpen}>
              {building ? <Icon name="pencil alternate" /> : <TranslationComponent stringKey={'Create building'} />}
            </Button>
          }
          open={modalOpen}
          onClose={handleModalClose}
          closeOnDimmerClick={false}
          size={'fullscreen'}
        >
          <Modal.Header>
            <TranslationComponent stringKey={building ? 'Edit Building' : 'New Building'} />
          </Modal.Header>
          <Modal.Content>
            <Form id="editBuildingForm" size="large" error loading={isSubmitting && loading} onSubmit={handleSubmit}>
              <Modal.Description>
                <Field name="name">
                  {({ field }: FieldProps) => <Form.Input placeholder={t('Name')} fluid {...field} />}
                </Field>
                <ErrorMessage name="name">{(msg) => <Message error content={msg} />}</ErrorMessage>
                {error && <Message error content={error.response.data} />}
                <Field name="buildingArea">
                  {({ field, form }: FieldProps) => (
                    <MapAreaSelector
                      onSelected={(rect) => form.setFieldValue(field.name, rect)}
                      selection={field.value}
                    />
                  )}
                </Field>
                <ErrorMessage name="buildingArea">{(msg) => <Message error visible content={msg} />}</ErrorMessage>
              </Modal.Description>
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={handleModalClose}>
              <TranslationComponent stringKey={'Cancel'} />
            </Button>
            <Button primary form="editBuildingForm" type="submit">
              <TranslationComponent stringKey={'Confirm'} />
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </Formik>
  );
};

export const BuildingUpsertModal = connect(null, { buildingUpsertAction })(PBuildingUpsertModal);
